import { createSlice } from "@reduxjs/toolkit";
import {
  fetchTimeEntries,
  fetchAllTimeEntries,
  fetchMyWorkItemsAll,
  fetchAllWorkItemsForLogPicker,
  fetchTeamTimeSummary,
  fetchProjectTimeSummary,
  fetchDeveloperTimeSummary,
  updateMyTimeLog,
  deleteMyTimeLog,
} from "../Thunks/timeEntryThunk";
import { normalizeMyTimeLogsResponse } from "../../Pages/TimeEntry/timeEntryMappers";
import {
  mapTeamTimeSummaryFromThunkPayload,
  mapProjectTimeSummaryFromThunkPayload,
  mapDeveloperTimeSummaryFromThunkPayload,
} from "../../Pages/TimeEntry/timeEntryApiMappers";
import {
  addAsyncResourceThunk,
  applyBranchRejected,
  isSupersededRequestPayload,
} from "../helpers/asyncResource";

const initialState = {
  data: null,
  loading: false,
  error: null,
  allTimeEntriesData: null,
  allTimeEntriesLoading: false,
  allTimeEntriesError: null,
  myTimeLogSaving: false,
  myTimeLogError: null,
  teamSummaryData: null,
  teamSummaryLoading: false,
  teamSummaryError: null,
  projectSummaryData: null,
  projectSummaryLoading: false,
  projectSummaryError: null,
  developerSummaryData: null,
  developerSummaryLoading: false,
  developerSummaryError: null,
  /** GET /work-items/my/all — Time Entry task filter dropdown (does not use issues slice). */
  myWorkItemsPickerList: [],
  myWorkItemsPickerLoading: false,
  myWorkItemsPickerError: null,
  /** GET /work-items/all — Log Time modal issue picker (unpaginated all work items user can access). */
  logPickerWorkItemsList: [],
  logPickerWorkItemsLoading: false,
  logPickerWorkItemsError: null,
};

export const timeEntrySlice = createSlice({
  name: "timeEntries",
  initialState,
  reducers: {
    clearTimeEntries: (state) => {
      state.data = null;
      state.error = null;
    },
    updateBillingTag: (state, action) => {
      const { entryId, newTag } = action.payload;
      if (state.data?.groups) {
        state.data.groups.forEach((group) => {
          group.entries.forEach((entry) => {
            if (entry.id === entryId) {
              entry.billingTag = newTag;
            }
          });
        });
      } else if (state.data?.list) {
        state.data.list.forEach((entry) => {
          if (entry.id === entryId) {
            entry.billingTag = newTag;
          }
        });
      }
    },
  },
  extraReducers: (builder) => {
    addAsyncResourceThunk(builder, fetchTimeEntries, {
      onFulfilled: (state, action) => {
        state.data = normalizeMyTimeLogsResponse(action.payload);
      },
    });
    builder
      .addCase(fetchAllTimeEntries.pending, (state) => {
        state.allTimeEntriesLoading = true;
        state.allTimeEntriesError = null;
      })
      .addCase(fetchAllTimeEntries.fulfilled, (state, action) => {
        state.allTimeEntriesLoading = false;
        state.allTimeEntriesError = null;
        state.allTimeEntriesData = normalizeMyTimeLogsResponse(action.payload);
      })
      .addCase(fetchAllTimeEntries.rejected, (state, action) => {
        applyBranchRejected(
          state,
          action,
          { loadingKey: "allTimeEntriesLoading", errorKey: "allTimeEntriesError" },
          "Failed to fetch time entries"
        );
      });
    builder
      .addCase(fetchMyWorkItemsAll.pending, (state) => {
        state.myWorkItemsPickerLoading = true;
        state.myWorkItemsPickerError = null;
      })
      .addCase(fetchMyWorkItemsAll.fulfilled, (state, action) => {
        state.myWorkItemsPickerLoading = false;
        state.myWorkItemsPickerError = null;
        state.myWorkItemsPickerList = Array.isArray(action.payload) ? action.payload : [];
      })
      .addCase(fetchMyWorkItemsAll.rejected, (state, action) => {
        state.myWorkItemsPickerLoading = false;
        if (isSupersededRequestPayload(action.payload)) return;
        state.myWorkItemsPickerError =
          action.payload ?? action.error?.message ?? "Failed to fetch work items";
      })
      .addCase(fetchAllWorkItemsForLogPicker.pending, (state) => {
        state.logPickerWorkItemsLoading = true;
        state.logPickerWorkItemsError = null;
      })
      .addCase(fetchAllWorkItemsForLogPicker.fulfilled, (state, action) => {
        state.logPickerWorkItemsLoading = false;
        state.logPickerWorkItemsError = null;
        state.logPickerWorkItemsList = Array.isArray(action.payload) ? action.payload : [];
      })
      .addCase(fetchAllWorkItemsForLogPicker.rejected, (state, action) => {
        state.logPickerWorkItemsLoading = false;
        if (isSupersededRequestPayload(action.payload)) return;
        state.logPickerWorkItemsError =
          action.payload ?? action.error?.message ?? "Failed to fetch work items";
      })
      .addCase(updateMyTimeLog.pending, (state) => {
        state.myTimeLogSaving = true;
        state.myTimeLogError = null;
      })
      .addCase(updateMyTimeLog.fulfilled, (state) => {
        state.myTimeLogSaving = false;
        state.myTimeLogError = null;
      })
      .addCase(updateMyTimeLog.rejected, (state, action) => {
        applyBranchRejected(
          state,
          action,
          { loadingKey: "myTimeLogSaving", errorKey: "myTimeLogError" },
          "Failed to update time log"
        );
      })
      .addCase(deleteMyTimeLog.pending, (state) => {
        state.myTimeLogSaving = true;
        state.myTimeLogError = null;
      })
      .addCase(deleteMyTimeLog.fulfilled, (state) => {
        state.myTimeLogSaving = false;
        state.myTimeLogError = null;
      })
      .addCase(deleteMyTimeLog.rejected, (state, action) => {
        applyBranchRejected(
          state,
          action,
          { loadingKey: "myTimeLogSaving", errorKey: "myTimeLogError" },
          "Failed to delete time log"
        );
      })
      .addCase(fetchTeamTimeSummary.pending, (state) => {
        state.teamSummaryLoading = true;
        state.teamSummaryError = null;
      })
      .addCase(fetchTeamTimeSummary.fulfilled, (state, action) => {
        state.teamSummaryLoading = false;
        state.teamSummaryError = null;
        state.teamSummaryData = mapTeamTimeSummaryFromThunkPayload(action.payload);
      })
      .addCase(fetchTeamTimeSummary.rejected, (state, action) => {
        applyBranchRejected(
          state,
          action,
          { loadingKey: "teamSummaryLoading", errorKey: "teamSummaryError" },
          "Failed to fetch team time summary"
        );
      })
      .addCase(fetchProjectTimeSummary.pending, (state) => {
        state.projectSummaryLoading = true;
        state.projectSummaryError = null;
      })
      .addCase(fetchProjectTimeSummary.fulfilled, (state, action) => {
        state.projectSummaryLoading = false;
        state.projectSummaryError = null;
        state.projectSummaryData = mapProjectTimeSummaryFromThunkPayload(action.payload);
      })
      .addCase(fetchProjectTimeSummary.rejected, (state, action) => {
        applyBranchRejected(
          state,
          action,
          { loadingKey: "projectSummaryLoading", errorKey: "projectSummaryError" },
          "Failed to fetch project time summary"
        );
      })
      .addCase(fetchDeveloperTimeSummary.pending, (state) => {
        state.developerSummaryLoading = true;
        state.developerSummaryError = null;
      })
      .addCase(fetchDeveloperTimeSummary.fulfilled, (state, action) => {
        state.developerSummaryLoading = false;
        state.developerSummaryError = null;
        state.developerSummaryData = mapDeveloperTimeSummaryFromThunkPayload(action.payload);
      })
      .addCase(fetchDeveloperTimeSummary.rejected, (state, action) => {
        applyBranchRejected(
          state,
          action,
          { loadingKey: "developerSummaryLoading", errorKey: "developerSummaryError" },
          "Failed to fetch developer time summary"
        );
      });
  },
});

export const { clearTimeEntries, updateBillingTag } = timeEntrySlice.actions;
export const selectTimeEntriesData = (state) => state.timeEntries.data;
export const selectTimeEntriesLoading = (state) => state.timeEntries.loading;
export const selectTimeEntriesError = (state) => state.timeEntries.error;
export const selectAllTimeEntriesData = (state) => state.timeEntries.allTimeEntriesData;
export const selectAllTimeEntriesLoading = (state) => state.timeEntries.allTimeEntriesLoading;
export const selectAllTimeEntriesError = (state) => state.timeEntries.allTimeEntriesError;
export const selectMyTimeLogSaving = (state) => state.timeEntries.myTimeLogSaving;
export const selectMyTimeLogError = (state) => state.timeEntries.myTimeLogError;
export const selectTeamSummaryData = (state) => state.timeEntries.teamSummaryData;
export const selectTeamSummaryLoading = (state) => state.timeEntries.teamSummaryLoading;
export const selectTeamSummaryError = (state) => state.timeEntries.teamSummaryError;
export const selectProjectSummaryData = (state) => state.timeEntries.projectSummaryData;
export const selectProjectSummaryLoading = (state) => state.timeEntries.projectSummaryLoading;
export const selectProjectSummaryError = (state) => state.timeEntries.projectSummaryError;
export const selectDeveloperSummaryData = (state) => state.timeEntries.developerSummaryData;
export const selectDeveloperSummaryLoading = (state) => state.timeEntries.developerSummaryLoading;
export const selectDeveloperSummaryError = (state) => state.timeEntries.developerSummaryError;
export const selectMyWorkItemsPickerList = (state) => state.timeEntries.myWorkItemsPickerList;
export const selectMyWorkItemsPickerLoading = (state) => state.timeEntries.myWorkItemsPickerLoading;
export const selectMyWorkItemsPickerError = (state) => state.timeEntries.myWorkItemsPickerError;
export const selectLogPickerWorkItemsList = (state) => state.timeEntries.logPickerWorkItemsList;
export const selectLogPickerWorkItemsLoading = (state) =>
  state.timeEntries.logPickerWorkItemsLoading;
export const selectLogPickerWorkItemsError = (state) => state.timeEntries.logPickerWorkItemsError;

export default timeEntrySlice.reducer;
